import { formatDate, DATE_FORMATS } from '@frontend/mwm-ui'

import type { ReactElement, PropsWithChildren } from 'react'
import type { IAppUser, USER_TYPE } from '~/types/app-user'

import { EnvironmentBadge } from '~/components/Badges'
import Button from '~/components/Buttons/Button'
import { copyText } from '~/utils/navigator'

import './styles.scss'

const InfoBlock = ({
  children,
  title,
}: PropsWithChildren<{
  title: string
}>): ReactElement => {
  return (
    <div className="user-info-card__info-block">
      <div className="user-info-card__info-block__title">{title}</div>
      {children}
    </div>
  )
}

export interface Props {
  handleSeeMoreClick: VoidFunction
  idSearched: string
  user: IAppUser
  userType: USER_TYPE
}

const UserInfoCard = ({ handleSeeMoreClick, idSearched, user, userType }: Props): ReactElement => {
  /** Handlers */
  const handleCopy = (value: string) => () => copyText(value, 'Copied to clipboard!')

  return (
    <div className="user-info-card">
      <EnvironmentBadge className="user-info-card__env-badge" value={userType} />
      <Button className="user-info-card__icon-button" onClick={handleSeeMoreClick} size="small" type="button">
        View more
      </Button>
      <InfoBlock title="Details">
        <table className="table">
          <thead>
            <tr>
              <th className="table-header table-position--left">User ID</th>
              <th className="table-header table-position--right">Last seen</th>
              <th className="table-header table-position--right">Client ID</th>
              <th className="table-header table-position--right">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr className="table-row">
              <td className="table-cell table-cell--clickable table-cell--flexed" onClick={handleCopy(user.user_id)}>
                <span className={user.user_id === idSearched ? 'highlight-colored' : ''}>{user.user_id}</span>
              </td>
              <td className="table-cell table-position--right">
                {formatDate(user.last_seen, DATE_FORMATS.DEFAULT_WITH_HOURS)}
              </td>
              <td className="table-cell table-position--right" onClick={handleCopy(user.client_id)}>
                {user.client_id}
              </td>
              <td className="table-cell table-position--right">{user.status}</td>
            </tr>
          </tbody>
        </table>
        <div className="user-info-card__separator" />
      </InfoBlock>
      <InfoBlock title={`Email credentials (${user.email_credentials?.length})`}>
        <table className="table">
          <thead>
            <tr>
              <th className="table-header table-position--left">Email</th>
              <th className="table-header table-position--right">Created at</th>
              <th className="table-header table-position--right">Updated at</th>
            </tr>
          </thead>
          <tbody>
            {user.email_credentials ? (
              user.email_credentials.map(({ created_at, email, is_verified, updated_at }, index) => (
                <tr className="table-row" key={`${email}-${index}`}>
                  <td className="table-cell table-cell--clickable table-cell--flexed" onClick={handleCopy(email)}>
                    <span className={email === idSearched ? 'highlight-colored' : ''}>{email}</span>
                    {is_verified ? <div className="user-verified-badge">Verified</div> : null}
                  </td>
                  <td className="table-cell table-position--right">
                    {formatDate(created_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}
                  </td>
                  <td className="table-cell table-position--right">
                    {formatDate(updated_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}
                  </td>
                </tr>
              ))
            ) : (
              <div className="no-data-message">No email credentials</div>
            )}
          </tbody>
        </table>
        <div className="user-info-card__separator" />
      </InfoBlock>
      <InfoBlock title={`Connections (${user.connections?.length})`}>
        {user.connections && user.connections.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th className="table-header table-position--left">Connection ID</th>
                <th className="table-header table-position--right">Installation ID</th>
                <th className="table-header table-position--right">Application</th>
                <th className="table-header table-position--right">Status</th>
                <th className="table-header table-position--right">Created at</th>
                <th className="table-header table-position--right">Updated at</th>
              </tr>
            </thead>
            <tbody>
              {user.connections.map(
                ({ active, app_key, connection_id, created_at, installation_id, updated_at }, index) => (
                  <tr className="table-row" key={`${connection_id}-${index}`}>
                    <td className="table-cell table-cell--clickable" onClick={handleCopy(connection_id)}>
                      <span className={connection_id === idSearched ? 'highlight-colored' : ''}>{connection_id}</span>
                    </td>
                    <td
                      className="table-cell table-position--right table-cell--clickable"
                      onClick={handleCopy(installation_id)}
                    >
                      <span className={installation_id === idSearched ? 'highlight-colored' : ''}>
                        {installation_id}
                      </span>
                    </td>
                    <td
                      className="table-cell table-position--right table-cell--clickable"
                      onClick={handleCopy(app_key)}
                    >
                      {app_key}
                    </td>
                    <td className="table-cell table-position--right">{active ? 'Active' : 'Inactive'}</td>
                    <td className="table-cell table-position--right">
                      {formatDate(created_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}
                    </td>
                    <td className="table-cell table-position--right">
                      {formatDate(updated_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        ) : (
          <div className="no-data-message">No connections</div>
        )}

        <div className="user-info-card__separator" />
      </InfoBlock>
      <InfoBlock title={`Provider Credentials (${user.provider_credentials?.length})`}>
        {user.provider_credentials && user.provider_credentials?.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th className="table-header table-position--left">Provider ID</th>
                <th className="table-header table-position--right">Provider</th>
                <th className="table-header table-position--right">Created at</th>
              </tr>
            </thead>
            <tbody>
              {user.provider_credentials.map(({ created_at, provider, provider_user_id }, index) => (
                <tr className="table-row" key={`${provider_user_id}-${index}`}>
                  <td className="table-cell table-cell--clickable" onClick={handleCopy(provider_user_id)}>
                    <span className={provider_user_id === idSearched ? 'highlight-colored' : ''}>
                      {provider_user_id}
                    </span>
                  </td>
                  <td className="table-cell table-position--right table-cell--clickable" onClick={handleCopy(provider)}>
                    {provider}
                  </td>
                  <td className="table-cell table-position--right">
                    {formatDate(created_at, DATE_FORMATS.DEFAULT_WITH_HOURS)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-data-message">No provider credentials</div>
        )}
      </InfoBlock>
    </div>
  )
}

export default UserInfoCard
